import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  OnInit,
} from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { BehaviorSubject, Observer } from "rxjs";
import { Router } from "@angular/router";

import { ByBranch } from "../../../../model/filters/by-branch";
import { MatchAny } from "../../../../model/filters/match.any";
import { Filter } from "../../../../model/filters/filter";
import { SingleChoice } from "../../../../model/singlechoice";
import { BranchService } from "../../../../services/branch.service";
import { NavigationHelper } from "../../../../services/navigation.helper";
import { FilterService } from "../../../../services/filter.service";
import { Navigation } from "../../../../model/navigation";
import { Labeler } from "../../../../model/labeler";
import { NotComponent } from "../not/not.component";

@Component({
  selector: "by-branch-form",
  templateUrl: "./by-branch-form.component.html",
  styleUrl: "./by-branch-form.component.css",
})
export class ByBranchFormComponent implements OnInit {
  @Input() filter: ByBranch = new ByBranch();
  @Output() applyFilter = new EventEmitter<ByBranch>();
  @Output() choiceEvent: EventEmitter<SingleChoice> = new EventEmitter();

  byBranchForm: FormGroup;

  branchLabeler = new BehaviorSubject<string>("-");
  completionUrl = new BehaviorSubject<string>("tickets/-1/filter/by.branch");
  currentId: number | null;
  editingIndex: number | null = null;
  byBranchFilter: ByBranch = new ByBranch();
  @ViewChild(NotComponent) notComponent!: NotComponent;

  constructor(
    private fb: FormBuilder,
    private branchService: BranchService,
    private nav: NavigationHelper,
    private filterService: FilterService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const existingFilters =
      this.filter instanceof MatchAny ? this.filter.filters : [this.filter];

    const branchIdsFromFilter: number[] = existingFilters
      .filter((f): f is ByBranch => f instanceof ByBranch)
      .map((f) => f.id)
      .filter((id) => id !== undefined) as number[];

    const storedBranchIds = localStorage.getItem("branchIds");
    const branchIdsFromStorage = storedBranchIds
      ? (JSON.parse(storedBranchIds) as number[])
      : [];

    const mergedIds = Array.from(
      new Set([...branchIdsFromFilter, ...branchIdsFromStorage])
    );

    this.initializeForm(mergedIds);
    if (mergedIds.length > 0) {
      this.initializeForm(mergedIds);
    } else {
      this.byBranchForm = this.fb.group({
        branches: this.fb.array([this.fb.control(null, Validators.required)]),
      });
    }

    if (mergedIds.length > 0) {
      this.currentId = mergedIds[0];
      this.branchLabeler.next(this.branchService.labelForId(this.currentId));
    }
  }

  initializeForm(branchIds: number[]): void {
    const controls = branchIds.map((id) =>
      this.fb.control(id, Validators.required)
    );
    if (controls.length === 0)
      controls.push(this.fb.control(null, Validators.required));

    this.byBranchForm = this.fb.group({
      branches: this.fb.array(controls),
    });

    this.currentId = controls[0]?.value ?? null;
    if (this.currentId !== null) {
      this.branchLabeler.next(this.branchService.labelForId(this.currentId));
    }
  }

  restoreBranches(branchIds: (number | null)[]): void {
    this.byBranchForm.setControl("branches", this.fb.array([]));
    for (const id of branchIds) {
      this.branches.push(this.fb.control(id, Validators.required));
    }
  }

  apply(): void {
    const ids: number[] = this.branches.value.filter(
      (id: number | null | undefined): id is number =>
        id !== null && id !== undefined
    );

    if (ids.length === 0) return;

    localStorage.setItem("branchIds", JSON.stringify(ids));

    let finalFilter: Filter;
    if (ids.length === 1) {
      finalFilter = new ByBranch(ids[0]);
    } else {
      finalFilter = new MatchAny(ids.map((id) => new ByBranch(id)));
    }

    this.notComponent.filter = finalFilter;
    const modifiedFilter = this.notComponent.getModifiedFilter();

    this.applyFilter.emit(modifiedFilter);
 
  }

  onFilterChanged(finalFilter: Filter) {
    this.applyFilter.emit(finalFilter);
   
  }

  onBranchChoice(branch: number): void {
    if (this.editingIndex !== null && this.branches.at(this.editingIndex)) {
      this.branches.at(this.editingIndex).setValue(branch);
    } else if (this.branches.length > 0) {
      this.branches.at(0).setValue(branch);
    }

    const currentValues = this.branches.value.filter((id) => id !== null);
    localStorage.setItem("branchIds", JSON.stringify(currentValues));

    this.currentId = branch;

    this.router
      .navigate(["tickets/-1/filter/by.branch/" + branch])
      .then(() => this.nav.freshNavigation);
  }

  addBranchField(): void {
    this.branches.push(this.fb.control(null, Validators.required));

    const currentValues = this.branches.value.filter((id) => id !== null);
    localStorage.setItem("branchIds", JSON.stringify(currentValues));
  }

  removeField(index: number): void {
    if (this.branches.length > 1) {
      this.branches.removeAt(index);
      const currentValues = this.branches.value.filter((id) => id !== null);
      localStorage.setItem("branchIds", JSON.stringify(currentValues));
    }
  }

  setEditingIndex(index: number): void {
    this.editingIndex = index;
    localStorage.setItem("editingBranchIndex", index.toString());
  }

  relaySingleChoice(choice: SingleChoice) {
    this.nav.startChoice(choice);
  }

  get branches(): FormArray<FormControl> {
    return this.byBranchForm.get("branches") as FormArray<FormControl>;
  }

  get branchObserver(): Partial<Observer<number>> {
    return {
      next: (id) => {
        this.onBranchChoice(id);
      },
    };
  }

  get fakeId(): number {
    return Navigation.NEW;
  }

  get branchLabelerByService(): Labeler {
    return this.branchService;
  }
}
